import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { useTheme } from 'emotion-theming'
import { array, bool, number, object, oneOfType, shape, string } from 'prop-types'
import { generateLinkWithAddedParams, generateAffiliateLink, makeImgResizerUrl, pushGtmData } from '@jelly/utils'

// import components
import { AnchorTag, CheckMark, PhoneIcon, MonetizationButton } from '@jelly/components'
import { Button, Link } from '@jelly/ui'
// styled components via emotion
const TopCardWrapper = styled.div`
	background: ${({ theme }) => theme.colors.white};
	border-radius: 8px;
	box-shadow: ${({ theme }) => theme.shadows.greyMedium};
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
	margin: 0 auto 1.5rem auto;
	max-width: 248px;
	min-width: 200px;
	position: relative;
	width: 100%;
	@media (min-width: 420px) {
		margin: 0 auto 2rem auto;
		width: calc(50% - 1rem);
	}
	${({ theme }) => theme.MQ.S} {
		margin: 0 0.5rem 2rem 0;
	}
	${({ theme }) => theme.MQ.M} {
		width: calc(30% - 1rem);
	}
	${({ theme }) => theme.MQ.L} {
		min-width: 248px; // special case where we don't want the cards to shrink when they are at this breakpoint (3 - 2 layout)
		width: calc(25% - 1rem);
	}
	${({ theme }) => theme.MQ.XL} {
		margin: 0 8px;
		min-width: 200px;
		width: calc(1200px / 5 - 18px);
	}
	button {
		letter-spacing: 1px;
		margin: auto;
		max-width: 180px;
	}
`
const BestPickContext = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	height: 100%;
	margin: 0.75rem 1rem 1rem;
`
const BadgeWrapper = styled.div`
	${({ theme }) => theme.fonts.bold};
	align-items: center;
	background: ${({ isFeatured, theme }) =>
		isFeatured ? theme.colors.secondary : theme.bestPicks.badgeWrapperBackground};
	border-radius: 8px 8px 0 0;
	color: ${({ isFeatured, theme }) =>
		isFeatured ? theme.bestPicks.badgeWrapperFeaturedTextColor : theme.colors.white};
	display: flex;
	padding: 2px;
	height: 80px;
	width: 100%;
`
const BadgeTitle = styled.div`
	font-size: 16px;
	margin: auto;
	text-align: center;
`
const ServiceLogoAndNameWrapper = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-gap: 10px;
	align-items: center;
	height: 60px;

	${({ theme }) => theme.MQ.M} {
		grid-gap: 0px;
		height: 80px;
		grid-template-columns: 1fr;
	}
`
const ServiceLogoWrapper = styled.div`
	display: flex;
	min-height: 50px;
	margin: 0 auto;
	position: relative;

	${({ theme }) => theme.MQ.M} {
		max-width: 140px;
	}
`
const ServiceLogo = styled.img`
	bottom: 0;
	height: auto;
	left: 0;
	max-height: 100%;
	max-width: 140px; /* specified for ie11 */
	margin: auto;
	top: 0;
	right: 0;
	width: 100%;
`
const CompanyName = styled.div`
	color: ${({ theme }) => theme.bestPicks.companyNameColor};
	line-height: 1.2;
	margin: 0.25rem auto 0.5rem;
	max-height: 50px;
	text-align: center;
	width: fit-content;

	${({ theme }) => theme.MQ.M} {
		max-height: 18px;
	}

	${({ as, theme }) =>
		as &&
		`
		&:hover {
			color: ${theme.bestPicks.companyNameHoverColor};
			text-decoration: underline;
		}
	`}
`
const BulletsContainer = styled.div`
	display: block;
	height: 100%;
	margin: 0 auto;
`
const BulletWrapper = styled.div`
	display: flex;
	margin: 8px 0;
	flex-direction: row;
	svg {
		margin-top: 5px;
	}
`
const Bullet = styled.div`
	color: ${({ theme }) => theme.bestPicks.bulletTextColor};
	display: block;
	margin-left: 8px;
	width: 85%;
`
const ButtonsContainer = styled.div`
	align-items: center;
	display: flex;
	flex-direction: column;
	margin: 10px auto;
	/* min-height: 155px; */
	width: 100%;
`
const ClickToCallWrapper = styled(Link)`
	align-items: center;
	display: flex;
	height: 2rem;
	margin: 1rem auto 0;
	svg {
		fill: ${({ theme }) => theme.colors.tertiary};
	}
	&:hover {
		span {
			color: ${({ theme }) => theme.navigation.linkHoverColor};
		}
		svg {
			fill: ${({ theme }) => theme.navigation.linkHoverColor};
		}
	}
`
const ClickToCallTelLink = styled.span`
	color: ${({ theme }) => theme.colors.tertiary};
	font-weight: bold;
`
const ReadReviewWrapper = styled.div`
	text-align: center;
	width: 100%;
	max-width: 180px;
`

BestPickTopCard.propTypes = {
	bullets: array,
	categoryWidgetId: string,
	isFeaturedBestPick: bool,
	isJumpLink: bool,
	phoneNumber: string,
	pricing: object,
	rank: oneOfType([string, number]),
	review: shape({ hasReviewEnabled: bool, reviewContent: string }),
	reviewPath: string.isRequired,
	serviceName: string.isRequired,
	tabTitle: string.isRequired,
	thumbnail: string.isRequired,
}
/**
 * This is the Featured Service card
 *
 * @param {String} props.bullets :: list of info (strings)
 * @param {String} props.categoryWidgetId :: widget id set on the category level
 * @param {Boolean} props.isFeaturedBestPick :: flag to determine if it's a featured service
 * @param {Boolean} props.isJumpLink :: flag to determine if jumpLink is enabled in CCM
 * @param {String} props.phoneNumber :: string containing click to call phone number
 * @param {Object} props.pricing :: data for monetization
 * @param {String|Number} props.rank :: order ranking
 * @param {Object} props.review :: review data for service
 * @param {String} props.reviewPath :: string containing hyphonated anchor tag or review canonical
 * @param {String} props.serviceName :: name of service or product
 * @param {String} props.tabTitle :: name of service best of badge
 * @param {String} props.thumbnail :: url path for service logo
 */
export function BestPickTopCard({
	bullets,
	description,
	categoryWidgetId,
	isFeaturedBestPick,
	isJumpLink,
	phoneNumber,
	pricing,
	rank,
	review: { hasReviewEnabled, reviewContent },
	reviewPath,
	serviceName,
	tabTitle,
	templateType = 'mbp',
	thumbnail,
}) {
	const {
		bestPicks: { checkMark },
	} = useTheme()

	bullets = bullets ? bullets : !!description && [description.split('\n')[0]]

	const imgSrc = makeImgResizerUrl({ src: thumbnail, method: 'h', h: 50 })
	const cardType = 'bestPickTopCard'
	const [affiliateLink, setAffiliateLink] = useState(generateAffiliateLink(pricing))

	useEffect(() => {
		setAffiliateLink(
			generateLinkWithAddedParams({
				link: affiliateLink,
				rank: rank,
				type: cardType,
				tabTitle: tabTitle,
			})
		)
	}, []) //eslint-disable-line

	const handleRfqButtonClick = () => {
		pushGtmData({
			event: 'gaEvent',
			gaEventCategory: 'Compare Quotes Click',
			gaEventAction: serviceName,
			gaEventLabel: 'Top Best Pick Card',
			gaEventNoninteraction: false,
		})
	}
	const handleQtClick = (location = 'Button') => {
		pushGtmData({
			event: 'gaEvent',
			gaEventCategory: 'Visit Site Click',
			gaEventAction: serviceName,
			gaEventLabel: `Top Best Pick Card ${location}`,
			gaEventNoninteraction: false,
		})
	}

	return (
		<TopCardWrapper>
			<BadgeWrapper isFeatured={isFeaturedBestPick}>
				<BadgeTitle>{tabTitle}</BadgeTitle>
			</BadgeWrapper>
			<BestPickContext>
				<ServiceLogoAndNameWrapper>
					<ServiceLogoWrapper
						id={`mbp_${serviceName}_bestpick_company_logo`}
						{...(affiliateLink && {
							alt: serviceName,
							as: AnchorTag,
							href: affiliateLink,
							onClick: () => handleQtClick('Logo'),
						})}
					>
						<ServiceLogo className="lazyload" data-src={imgSrc} alt={`${serviceName}-logo`} />
					</ServiceLogoWrapper>
					<CompanyName
						id={`mbp_${serviceName}_bestpick_company_name`}
						{...(affiliateLink && {
							as: AnchorTag,
							href: affiliateLink,
							onClick: () => handleQtClick('Service Name'),
						})}
					>
						{serviceName}
					</CompanyName>
				</ServiceLogoAndNameWrapper>
				<ButtonsContainer>
					<MonetizationButton
						buttonLimit={2}
						categoryWidgetId={categoryWidgetId}
						onQtButtonClick={handleQtClick}
						onRfqButtonClick={handleRfqButtonClick}
						pricing={pricing}
						rank={rank}
						serviceName={serviceName}
						sectionType="bestpicks"
						showArrow={false}
						templateType="mbp"
						type={cardType}
						tabTitle={tabTitle}
						fallbackComponent={
							hasReviewEnabled &&
							reviewContent && (
								<ReadReviewWrapper>
									<Button href={reviewPath} variant="primary">
										Read Review
									</Button>
								</ReadReviewWrapper>
							)
						}
					/>
					{phoneNumber && (
						<ClickToCallWrapper href={`tel:${phoneNumber}`}>
							<>
								<PhoneIcon height="20px" width="20px" />
								<ClickToCallTelLink>{phoneNumber}</ClickToCallTelLink>
							</>
						</ClickToCallWrapper>
					)}
				</ButtonsContainer>
				<BulletsContainer>
					{bullets &&
						bullets.map((bullet, i) => (
							<BulletWrapper key={`bullet-${i}`}>
								<CheckMark height={17} width={17} fill={checkMark} />
								<Bullet key={i}>{bullet}</Bullet>{' '}
							</BulletWrapper>
						))}
				</BulletsContainer>
			</BestPickContext>
		</TopCardWrapper>
	)
}
